import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import CandidatesPagination from '../../candidates-list/components/CandidatesPagination';
import CallStatusBadge from './CallStatusBadge';
import { formatCallDuration } from '../../../lib/utils/formatCallDuration';
import { cn } from '../../../utils/cn';

const PAGE_SIZE = 10;

const CandidateCallsTable = ({ calls, className }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);

  const formatCallDateTime = (date) => (
    date ? format(new Date(date), 'dd-MM-yyyy HH:mm') : t('common.na')
  );

  const totalCount = calls?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(currentPage, pageCount - 1);

  const visibleCalls = useMemo(() => {
    if (!calls?.length) return [];
    const start = safePage * PAGE_SIZE;
    return calls.slice(start, start + PAGE_SIZE);
  }, [calls, safePage]);

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [calls]);

  if (!calls?.length) {
    return (
      <div className={cn('bg-card border border-border rounded-lg p-8 text-center', className)}>
        <p className="text-muted-foreground">{t('ai.candidate.emptyCalls')}</p>
      </div>
    );
  }

  return (
    <div className={cn('bg-card border border-border rounded-lg overflow-hidden', className)}>
      <div className="p-4 md:p-6 border-b border-border">
        <h2 className="text-lg md:text-xl font-semibold text-foreground">{t('ai.candidate.allCalls')}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap w-[170px]">
                {t('ai.calls.tableDateTime')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap w-[110px]">
                {t('ai.calls.tableDuration')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap w-[180px]">
                {t('ai.calls.tableStatus')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t('ai.calls.tableSummary')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visibleCalls.map((call) => {
              const summary = call.ai_summary?.trim?.() || t('ai.candidate.noSummary');
              const duration = call.callDuration ?? call.call_duration;

              return (
                <tr key={call.id} className="border-b border-border">
                  <td className="px-4 py-4 whitespace-nowrap align-top w-[170px]">
                    <p className="text-sm text-muted-foreground">
                      {formatCallDateTime(call.created_at)}
                    </p>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap align-top w-[110px]">
                    <p className="text-sm text-muted-foreground tabular-nums">
                      {formatCallDuration(duration, t('common.emDash'))}
                    </p>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap align-top w-[180px]">
                    <CallStatusBadge status={call.status} />
                  </td>
                  <td className="px-4 py-4 align-top">
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                      {summary}
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalCount > PAGE_SIZE && (
        <div className="p-4 md:p-6 border-t border-border">
          <CandidatesPagination
            currentPage={safePage}
            pageCount={pageCount}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            itemLabel="call"
          />
        </div>
      )}
    </div>
  );
};

export default CandidateCallsTable;
